/**
 * Calendar domain logic, free of React and of the calendar library.
 *
 * A calendar shows existing cards. The adapter below turns a card's deadline into the
 * representation the grid needs; the grid library's own data model never leaks out of
 * `CalendarGrid`. All times are LOCAL wall-clock values of the deadline's timezone (the
 * user's profile zone when it was set); the grid renders them as "floating" times, so no
 * browser-timezone conversion ever happens.
 *
 *  - START + END  -> an interval: timed (time grid) or date-only (all-day / multi-day);
 *  - END only     -> a deadline: a task in a month cell, never a block in the hour grid
 *                    (the Day/Week panel lists it instead). It never gets an invented START.
 */
import {
  type CalendarOccurrence,
  type Card,
  type DeadlineValue,
  hiddenCategories,
  type VisibilityPrefs,
} from '@/entities/board';
import { addDays, addMonths, isIsoDate, weekOf } from '@/shared/lib/calendar';

export type CalendarMode = 'day' | 'week' | 'workweek' | 'month';
export const CALENDAR_MODES: readonly CalendarMode[] = ['day', 'week', 'workweek', 'month'];

/**
 * How Week / Work week are drawn on a desktop: `compact` = day columns of short task rows with a
 * "+ N more" overflow (no hour grid), `hours` = the full time grid (drag, resize, create by
 * time). Day and Month have a single presentation.
 */
export type CalendarDensity = 'compact' | 'hours';
export const CALENDAR_DENSITIES: readonly CalendarDensity[] = ['compact', 'hours'];
const DENSITY_PARAM = 'cd';

/** Modes that have a density choice. */
export function hasDensity(mode: CalendarMode): boolean {
  return mode === 'week' || mode === 'workweek';
}

/** Snap step of creating, dragging and resizing in the hour grid, in minutes. */
export const SNAP_MINUTES = 15;
/** Duration of a task created by a click on an hour slot, in minutes. */
export const DEFAULT_DURATION_MINUTES = 60;

// --- ranges and navigation -------------------------------------------------

export interface DateRange {
  /** Inclusive ISO dates. */
  start: string;
  end: string;
}

/** ISO weekdays 1 (Monday) - 7 (Sunday) of the Work week when nothing else is chosen. */
export const DEFAULT_WORKWEEK: readonly number[] = [1, 2, 3, 4, 5];

/** ISO weekday of an ISO date: 1 = Monday ... 7 = Sunday. */
export function isoWeekday(date: string): number {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay() || 7;
}

/**
 * The dates of the Work week of the week that contains `date`: the selected ISO weekdays, in the
 * order they occur in that week (which starts on the account's first day). A selection with no
 * valid day falls back to Monday - Friday.
 */
export function workweekDates(
  date: string,
  firstDay: number,
  weekdays: readonly number[] = DEFAULT_WORKWEEK,
): string[] {
  const chosen = new Set(weekdays.filter((day) => day >= 1 && day <= 7));
  const set = chosen.size > 0 ? chosen : new Set(DEFAULT_WORKWEEK);
  const { first, last } = weekOf(date, firstDay);
  return eachDay({ start: first, end: last }).filter((day) => set.has(isoWeekday(day)));
}

/** The dates the grid shows (the month grid includes the neighbours' days). */
export function visibleRange(
  mode: CalendarMode,
  date: string,
  firstDay: number,
  workweek: readonly number[] = DEFAULT_WORKWEEK,
): DateRange {
  switch (mode) {
    case 'day':
      return { start: date, end: date };
    case 'week': {
      const week = weekOf(date, firstDay);
      return { start: week.first, end: week.last };
    }
    case 'workweek': {
      // From the first to the last selected day of the week (gaps between them are not shown).
      const days = workweekDates(date, firstDay, workweek);
      return { start: days[0] ?? date, end: days.at(-1) ?? date };
    }
    case 'month': {
      const first = `${date.slice(0, 8)}01`;
      const last = addDays(addMonths(first, 1), -1);
      return { start: weekOf(first, firstDay).first, end: weekOf(last, firstDay).last };
    }
  }
}

/** Previous / next period; weeks and work weeks both move by 7 days (the week stays aligned). */
export function shiftDate(mode: CalendarMode, date: string, direction: -1 | 1): string {
  if (mode === 'day') return addDays(date, direction);
  if (mode === 'month') return addMonths(date, direction);
  return addDays(date, 7 * direction);
}

export function eachDay(range: DateRange): string[] {
  const days: string[] = [];
  for (let day = range.start; day <= range.end && days.length < 100; day = addDays(day, 1)) {
    days.push(day);
  }
  return days;
}

// --- URL state ---------------------------------------------------------------

export interface CalendarState {
  mode: CalendarMode;
  density: CalendarDensity;
  /** Any date inside the shown period. */
  date: string;
  includeCompleted: boolean;
  includeOverdue: boolean;
  /** Show tasks (deadline-only and undated cards). */
  showTasks: boolean;
  /** Show events (cards with an explicit start and end). */
  showEvents: boolean;
  search: string;
  labelIds: string[];
  /** Workspace scope: boards switched off (everything else is shown, new boards included). */
  hiddenBoardIds: string[];
}

/** How many filters differ from the defaults (shown on the Filters button). */
export function activeFilterCount(state: CalendarState): number {
  return (
    hiddenCategories(visibilityOf(state)) +
    (state.search.trim() ? 1 : 0) +
    (state.labelIds.length > 0 ? 1 : 0) +
    (state.hiddenBoardIds.length > 0 ? 1 : 0)
  );
}

/** The four primary switches of a state, as a {@link VisibilityPrefs}. */
export function visibilityOf(state: CalendarState): VisibilityPrefs {
  return {
    showTasks: state.showTasks,
    showEvents: state.showEvents,
    showCompleted: state.includeCompleted,
    showOverdue: state.includeOverdue,
  };
}

const MODE_PARAM = 'cal';

export function isCalendarOpen(params: URLSearchParams): boolean {
  return CALENDAR_MODES.includes(params.get(MODE_PARAM) as CalendarMode);
}

/** URL parameter of each primary switch (`0` = off, `1` = on; absent = the stored preference). */
const SWITCH_PARAMS = [
  ['showTasks', 'tasks'],
  ['showEvents', 'events'],
  ['includeCompleted', 'done'],
  ['includeOverdue', 'late'],
] as const;

const STORED_NAME = {
  showTasks: 'showTasks',
  showEvents: 'showEvents',
  includeCompleted: 'showCompleted',
  includeOverdue: 'showOverdue',
} as const;

const ALL_VISIBLE: VisibilityPrefs = {
  showTasks: true,
  showEvents: true,
  showCompleted: true,
  showOverdue: true,
};

/**
 * Reads the calendar state from the query string (unknown or invalid values fall back).
 *
 * One source of truth per value: a switch that is IN the URL wins (a shared or reloaded link
 * looks the same); one that is not falls back to the scope's stored preference (`stored`), then
 * to "shown". Writing keeps both in step (see {@link writeCalendarState}), so they never
 * contradict each other.
 */
export interface CalendarDefaults {
  /** The view when the URL names none (differs per scope and per device class). */
  mode: CalendarMode;
  density: CalendarDensity;
}

const FALLBACK_DEFAULTS: CalendarDefaults = { mode: 'week', density: 'hours' };

export function readCalendarState(
  params: URLSearchParams,
  today: string,
  stored: VisibilityPrefs = ALL_VISIBLE,
  defaults: CalendarDefaults = FALLBACK_DEFAULTS,
): CalendarState {
  const mode = params.get(MODE_PARAM) as CalendarMode;
  const density = params.get(DENSITY_PARAM) as CalendarDensity;
  const date = params.get('date') ?? '';
  const list = (key: string) => (params.get(key) ?? '').split(',').filter(Boolean);
  const flag = (param: string, fallback: boolean) => {
    const value = params.get(param);
    return value === '0' ? false : value === '1' ? true : fallback;
  };
  return {
    mode: CALENDAR_MODES.includes(mode) ? mode : defaults.mode,
    density: CALENDAR_DENSITIES.includes(density) ? density : defaults.density,
    date: isIsoDate(date) ? date : today,
    showTasks: flag('tasks', stored.showTasks),
    showEvents: flag('events', stored.showEvents),
    includeCompleted: flag('done', stored.showCompleted),
    includeOverdue: flag('late', stored.showOverdue),
    search: params.get('cq') ?? '',
    labelIds: list('cl'),
    hiddenBoardIds: list('hide'),
  };
}

/**
 * Writes the state into a copy of `params`, keeping unrelated parameters. A switch that is OFF is
 * always written (so a shared or reloaded link looks the same); one that is ON is written only
 * when a bare URL would mean OFF (the stored preference). A default URL therefore stays clean.
 */
export function writeCalendarState(
  params: URLSearchParams,
  state: CalendarState,
  stored: VisibilityPrefs = ALL_VISIBLE,
  defaults: CalendarDefaults = FALLBACK_DEFAULTS,
): URLSearchParams {
  const next = new URLSearchParams(params);
  const set = (key: string, value: string | null) => {
    if (value) next.set(key, value);
    else next.delete(key);
  };
  set(MODE_PARAM, state.mode);
  // Written only where it matters and differs from the default, so a link stays clean.
  set(
    DENSITY_PARAM,
    hasDensity(state.mode) && state.density !== defaults.density ? state.density : null,
  );
  set('date', state.date);
  for (const [field, param] of SWITCH_PARAMS) {
    // "Off" is always written (a shared link looks the same); "on" only when a bare URL would
    // mean "off" (the stored preference), so a default URL stays clean.
    const explicit = !state[field] || !stored[STORED_NAME[field]];
    set(param, explicit ? (state[field] ? '1' : '0') : null);
  }
  set('cq', state.search.trim() || null);
  set('cl', state.labelIds.join(',') || null);
  set('hide', state.hiddenBoardIds.join(',') || null);
  return next;
}

/** Leaves the calendar: removes every calendar parameter. */
export function clearCalendarState(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of [
    MODE_PARAM,
    DENSITY_PARAM,
    'date',
    'done',
    'late',
    'tasks',
    'events',
    'cq',
    'cl',
    'hide',
  ]) {
    next.delete(key);
  }
  return next;
}

// --- remembered view per device class ----------------------------------------

const VIEW_PREFIX = 'zadok.calendarView.';

/**
 * The view last chosen on this kind of device ("desktop" or "phone") for one calendar scope.
 * The two are stored apart on purpose: opening the app on a phone for a minute must not
 * overwrite what the desktop shows, and the other way round. Storage that is unavailable just
 * means "not remembered".
 */
export function readRememberedView(
  storageKey: string,
  device: 'desktop' | 'phone',
): { mode?: CalendarMode; density?: CalendarDensity } {
  try {
    const raw = window.localStorage.getItem(`${VIEW_PREFIX}${storageKey}.${device}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { mode?: unknown; density?: unknown };
    return {
      ...(CALENDAR_MODES.includes(parsed.mode as CalendarMode) && {
        mode: parsed.mode as CalendarMode,
      }),
      ...(CALENDAR_DENSITIES.includes(parsed.density as CalendarDensity) && {
        density: parsed.density as CalendarDensity,
      }),
    };
  } catch {
    return {};
  }
}

export function rememberView(
  storageKey: string,
  device: 'desktop' | 'phone',
  view: { mode: CalendarMode; density: CalendarDensity },
): void {
  try {
    window.localStorage.setItem(`${VIEW_PREFIX}${storageKey}.${device}`, JSON.stringify(view));
  } catch {
    // Private mode / quota: the choice just is not remembered.
  }
}

/**
 * The view a calendar opens in when nothing names one: the one last chosen on this kind of device
 * for the scope, else a default - a phone starts on its day, the personal planner on the month,
 * boards and workspaces on the week.
 */
export function defaultCalendarView(
  storageKey: string,
  device: 'desktop' | 'phone',
  personal = false,
): CalendarDefaults {
  const remembered = readRememberedView(storageKey, device);
  return {
    mode: remembered.mode ?? (device === 'phone' ? 'day' : personal ? 'month' : 'week'),
    density: remembered.density ?? (personal ? 'compact' : 'hours'),
  };
}

// --- local date-times --------------------------------------------------------

/** `YYYY-MM-DDTHH:MM` -> its parts. */
export function splitLocal(value: string): { date: string; time: string } {
  return { date: value.slice(0, 10), time: value.slice(11, 16) };
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** A "floating" Date (UTC fields = the wall-clock fields) -> `YYYY-MM-DDTHH:MM`. */
export function floatingToLocal(value: Date): string {
  return (
    `${String(value.getUTCFullYear()).padStart(4, '0')}-${pad(value.getUTCMonth() + 1)}-` +
    `${pad(value.getUTCDate())}T${pad(value.getUTCHours())}:${pad(value.getUTCMinutes())}`
  );
}

/** `YYYY-MM-DDTHH:MM` + minutes (may cross midnight). */
export function addMinutes(value: string, minutes: number): string {
  const { date, time } = splitLocal(value);
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  const [hours = 0, mins = 0] = time.split(':').map(Number);
  return floatingToLocal(new Date(Date.UTC(year, month - 1, day, hours, mins + minutes)));
}

/** The current moment as wall-clock time in an IANA zone, as a floating Date. */
export function floatingNow(timeZone: string | null, now: Date = new Date()): Date {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
      ...(timeZone && { timeZone }),
    }).formatToParts(now);
    const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
    return new Date(
      Date.UTC(
        get('year'),
        get('month') - 1,
        get('day'),
        get('hour'),
        get('minute'),
        get('second'),
      ),
    );
  } catch {
    return new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  }
}

// --- card deadline -> calendar event ---------------------------------------------

/** `interval` = an EVENT (explicit start and end); `deadline` = a TASK with a due date. */
export type EventKind = 'interval' | 'deadline';

export interface CalendarEvent {
  /** The card id (the calendar has no entities of its own). */
  id: string;
  card: Card;
  boardId: string;
  kind: EventKind;
  /** Date-only: shown in the all-day row / as a whole-day item. */
  allDay: boolean;
  /** `YYYY-MM-DD` (all-day) or `YYYY-MM-DDTHH:MM`. */
  start: string;
  /** All-day: EXCLUSIVE end date; timed: the end moment; `null` for a deadline. */
  end: string | null;
  /**
   * Set for an upcoming occurrence of a recurring series: `id` is then its occurrence key (not a
   * card id), `card` a stand-in for drawing, and it can be opened but never dragged or resized.
   */
  occurrence?: CalendarOccurrence;
  /**
   * Derived presentation state of an EVENT (interval) against "now": never stored, never a
   * completion. Absent for deadline-only tasks (those keep their overdue semantics).
   */
  phase?: EventPhase;
}

/** Where an event is in time: not yet started, happening now, or ended (by its own end). */
export type EventPhase = 'upcoming' | 'current' | 'past';

const clock = (time: string | null) => (time ? time.slice(0, 5) : null);

/** `null` for a card without a deadline. */
export function toEvent(card: Card): CalendarEvent | null {
  const deadline = card.deadline;
  if (!deadline) return null;
  const base = { id: card.id, card, boardId: card.placement.board_id };
  const endTime = clock(deadline.time);
  if (deadline.start_date === null) {
    return endTime
      ? {
          ...base,
          kind: 'deadline',
          allDay: false,
          start: `${deadline.date}T${endTime}`,
          end: null,
        }
      : { ...base, kind: 'deadline', allDay: true, start: deadline.date, end: null };
  }
  const startTime = clock(deadline.start_time);
  if (!startTime && !endTime) {
    return {
      ...base,
      kind: 'interval',
      allDay: true,
      start: deadline.start_date,
      end: addDays(deadline.date, 1),
    };
  }
  const start = `${deadline.start_date}T${startTime ?? '00:00'}`;
  // A date-only end finishes with its day (the next midnight).
  let end = endTime ? `${deadline.date}T${endTime}` : `${addDays(deadline.date, 1)}T00:00`;
  // The grid needs a visible block: never shorter than one snap step.
  if (end <= start) end = addMinutes(start, SNAP_MINUTES);
  return { ...base, kind: 'interval', allDay: false, start, end };
}

export function toEvents(cards: readonly Card[]): CalendarEvent[] {
  return cards.flatMap((card) => toEvent(card) ?? []);
}

/** A card-shaped stand-in for an occurrence, only so it is drawn like any other item. */
function standIn(occurrence: CalendarOccurrence): Card {
  return {
    id: occurrence.key,
    title: occurrence.title,
    completed_at: null,
    version: 0,
    created_at: '',
    updated_at: '',
    placement: {
      id: occurrence.key,
      board_id: occurrence.board_id,
      column_id: occurrence.column_id,
      position_key: '',
      is_pinned: false,
    },
    label_ids: occurrence.label_ids,
    checklist_progress: null,
    checklists: [],
    deadline: occurrence.deadline,
    is_overdue: false,
    cover: null,
    appearance: occurrence.appearance,
    attachment_count: 0,
    link_preview: null,
    assignment_kind: 'unassigned',
    assignee_ids: [],
  };
}

/** The virtual occurrences of a range as calendar events (same shapes as real cards). */
export function toOccurrenceEvents(occurrences: readonly CalendarOccurrence[]): CalendarEvent[] {
  return occurrences.flatMap((occurrence) => {
    const event = toEvent(standIn(occurrence));
    return event ? [{ ...event, occurrence }] : [];
  });
}

/** Day / Week / Work week show deadline-only tasks in the side panel, not in the hour grid. */
export function gridEvents(events: readonly CalendarEvent[], mode: CalendarMode): CalendarEvent[] {
  return mode === 'month' ? [...events] : events.filter((event) => event.kind === 'interval');
}

/** A task without any date: listed in the panel's "No date" section, never given a date. */
export interface UndatedTask {
  id: string;
  card: Card;
  boardId: string;
}

export function toUndated(cards: readonly Card[]): UndatedTask[] {
  return cards
    .filter((card) => card.deadline === null)
    .map((card) => ({ id: card.id, card, boardId: card.placement.board_id }));
}

export interface TaskGroups {
  /** Dated tasks of the visible range grouped by their due date, earliest first. */
  dated: { date: string; items: CalendarEvent[] }[];
  undated: UndatedTask[];
}

/**
 * The task panel content: tasks (deadline-only cards) whose due date is in the range, grouped by
 * date and ordered by time inside a date (date-only tasks after the timed ones), followed by
 * the undated tasks. Events never appear here.
 */
export function taskGroups(
  events: readonly CalendarEvent[],
  undated: readonly UndatedTask[],
  range: DateRange,
): TaskGroups {
  const inRange = events
    .filter((event) => event.kind === 'deadline')
    .filter(
      (event) => event.start.slice(0, 10) >= range.start && event.start.slice(0, 10) <= range.end,
    )
    .toSorted(
      (a, b) =>
        a.start.slice(0, 10).localeCompare(b.start.slice(0, 10)) ||
        Number(a.allDay) - Number(b.allDay) ||
        a.start.localeCompare(b.start) ||
        a.id.localeCompare(b.id),
    );
  const dated: TaskGroups['dated'] = [];
  for (const event of inRange) {
    const date = event.start.slice(0, 10);
    const last = dated.at(-1);
    if (last?.date === date) last.items.push(event);
    else dated.push({ date, items: [event] });
  }
  return { dated, undated: [...undated] };
}

/** Events / tasks the user switched off are removed from a list of calendar events. */
export function filterByKind(
  events: readonly CalendarEvent[],
  state: Pick<CalendarState, 'showTasks' | 'showEvents'>,
): CalendarEvent[] {
  return events.filter((event) => (event.kind === 'interval' ? state.showEvents : state.showTasks));
}

/** The last day (inclusive) an event occupies. */
function lastDayOf(event: CalendarEvent): string {
  const first = event.start.slice(0, 10);
  if (event.kind === 'deadline' || event.end === null) return first;
  if (event.allDay) return addDays(event.end, -1);
  const { date, time } = splitLocal(event.end);
  // An interval that ends exactly at midnight does not occupy the new day.
  const last = time === '00:00' ? addDays(date, -1) : date;
  return last < first ? first : last;
}

/** Intervals and deadlines that touch `day`, for the Agenda and the "+N" day details. */
export function eventsOnDay(events: readonly CalendarEvent[], day: string): CalendarEvent[] {
  return events
    .filter((event) => event.start.slice(0, 10) <= day && day <= lastDayOf(event))
    .toSorted((a, b) => Number(!a.allDay) - Number(!b.allDay) || a.start.localeCompare(b.start));
}

// --- grid interaction -> deadline -------------------------------------------------

interface Original {
  kind: EventKind;
  /** The card's END had no time / its START had no time. */
  endDateOnly: boolean;
  startDateOnly: boolean;
}

export function originalOf(event: CalendarEvent): Original {
  const deadline = event.card.deadline;
  return {
    kind: event.kind,
    endDateOnly: deadline?.time == null,
    startDateOnly: deadline?.start_time == null,
  };
}

/**
 * The deadline after the user moved or resized an event in the grid.
 * `start`/`end` are the event's new floating times (`end` exclusive for all-day events).
 *
 *  - deadline-only stays deadline-only: only the END date changes; a time is kept if there was
 *    one, a date-only deadline stays date-only;
 *  - an interval keeps its shape: all-day -> dates only, timed -> date + time; a date-only
 *    edge that lands on midnight stays date-only (so moving never invents times).
 */
export function deadlineFromEvent(
  original: Original,
  allDay: boolean,
  start: Date,
  end: Date | null,
): DeadlineValue {
  const startLocal = floatingToLocal(start);
  const startParts = splitLocal(startLocal);
  if (original.kind === 'deadline') {
    return {
      date: startParts.date,
      time: original.endDateOnly ? null : startParts.time,
      start_date: null,
      start_time: null,
    };
  }
  if (allDay) {
    const firstDay = startParts.date;
    const lastDay = end ? addDays(splitLocal(floatingToLocal(end)).date, -1) : firstDay;
    return {
      date: lastDay < firstDay ? firstDay : lastDay,
      time: null,
      start_date: firstDay,
      start_time: null,
    };
  }
  const endLocal = end ? floatingToLocal(end) : addMinutes(startLocal, DEFAULT_DURATION_MINUTES);
  const endParts = splitLocal(endLocal);
  const midnightEnd = endParts.time === '00:00' && original.endDateOnly;
  return {
    date: midnightEnd ? addDays(endParts.date, -1) : endParts.date,
    time: midnightEnd ? null : endParts.time,
    start_date: startParts.date,
    start_time: startParts.time === '00:00' && original.startDateOnly ? null : startParts.time,
  };
}

/**
 * A deadline-only task dragged from the side panel onto the grid. It stays deadline-only:
 * the END moves to the drop position (date, and the time when dropped on the hour grid).
 * Dropped on a whole-day target it moves to that date and keeps its time (if any).
 */
export function deadlineFromPanelDrop(
  event: Pick<CalendarEvent, 'card'>,
  dropped: Date,
  allDay: boolean,
): DeadlineValue {
  const parts = splitLocal(floatingToLocal(dropped));
  const hadTime = event.card.deadline?.time != null;
  const time = allDay ? (hadTime ? clock(event.card.deadline?.time ?? null) : null) : parts.time;
  return { date: parts.date, time, start_date: null, start_time: null };
}

/** The default new task for a click / selection (before the user edits it in the popup). */
export interface CreateDraft {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

/** A click on an hour slot: one hour from there. */
export function draftFromSlot(start: Date): CreateDraft {
  const from = floatingToLocal(start);
  const to = addMinutes(from, DEFAULT_DURATION_MINUTES);
  return {
    startDate: splitLocal(from).date,
    startTime: splitLocal(from).time,
    endDate: splitLocal(to).date,
    endTime: splitLocal(to).time,
  };
}

/** A drag over the hour grid: exactly the selected interval. */
export function draftFromSelection(start: Date, end: Date): CreateDraft {
  const from = splitLocal(floatingToLocal(start));
  const to = splitLocal(floatingToLocal(end));
  return { startDate: from.date, startTime: from.time, endDate: to.date, endTime: to.time };
}

/** A click or drag over whole days (month cells, the all-day row): `end` is exclusive. */
export function draftFromDays(firstDay: string, exclusiveEnd: string | null): CreateDraft {
  const last = exclusiveEnd ? addDays(exclusiveEnd, -1) : firstDay;
  return {
    startDate: firstDay,
    startTime: '',
    endDate: last < firstDay ? firstDay : last,
    endTime: '',
  };
}

// --- titles -------------------------------------------------------------------------

function utc(value: string): Date {
  const [year = 1970, month = 1, day = 1] = value.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

/** `October 2026`, `5 – 11 October 2026`, `Thursday, 8 October 2026`. */
export function periodTitle(
  mode: CalendarMode,
  date: string,
  range: DateRange,
  locale: string,
): string {
  const format = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' });
  if (mode === 'month') return format({ month: 'long', year: 'numeric' }).format(utc(date));
  if (mode === 'day') {
    return format({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(
      utc(date),
    );
  }
  const sameYear = range.start.slice(0, 4) === range.end.slice(0, 4);
  const span = format({
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).formatRange(utc(range.start), utc(range.end));
  return sameYear ? `${span} ${range.end.slice(0, 4)}` : span;
}

// --- presentation: phase, time ranges, workload ---------------------------------------------

/** The current wall-clock moment of a zone as `YYYY-MM-DDTHH:MM`. */
export function localNow(timeZone: string | null, now: Date = new Date()): string {
  return floatingToLocal(floatingNow(timeZone, now));
}

/**
 * Upcoming / current / past of an event by its actual start and end (the all-day end is
 * exclusive, so a one-day event is past from the next midnight). `null` for a deadline-only task.
 */
export function phaseOf(event: CalendarEvent, nowLocal: string): EventPhase | null {
  if (event.kind !== 'interval' || event.end === null) return null;
  if (event.allDay) {
    const today = nowLocal.slice(0, 10);
    if (event.end <= today) return 'past';
    return event.start <= today ? 'current' : 'upcoming';
  }
  if (event.end <= nowLocal) return 'past';
  return event.start <= nowLocal ? 'current' : 'upcoming';
}

/** The events with their {@link EventPhase} set. */
export function withPhases(events: readonly CalendarEvent[], nowLocal: string): CalendarEvent[] {
  return events.map((event) => {
    const phase = phaseOf(event, nowLocal);
    return phase ? { ...event, phase } : event;
  });
}

/**
 * "Hide past events": drops the events that have ended by their own end. A completed event that
 * has not ended yet stays; tasks (deadlines) are never hidden by time.
 */
export function withoutPast(events: readonly CalendarEvent[]): CalendarEvent[] {
  return events.filter((event) => event.phase !== 'past');
}

/**
 * `09:00–13:30` for a timed event; `22:00–02:00 +1` when it ends on a later day (the offset is
 * in days; an end at exactly midnight is shown as `00:00` without an offset). `null` for all-day
 * events and deadlines, which have no interval to show.
 */
export function timeRangeLabel(event: CalendarEvent): string | null {
  if (event.kind !== 'interval' || event.allDay || event.end === null) return null;
  const from = splitLocal(event.start);
  const to = splitLocal(event.end);
  const text = `${from.time}–${to.time}`;
  if (to.date === from.date) return text;
  if (to.time === '00:00' && to.date === addDays(from.date, 1)) return text;
  const days = Math.round((utcMs(to.date) - utcMs(from.date)) / 86_400_000);
  return `${text} +${String(days)}`;
}

/** The part of a timed event that falls on `day`: `09:00–13:30`, `22:00–24:00`, `00:00–02:00`. */
export function timeRangeOnDay(event: CalendarEvent, day: string): string | null {
  if (event.kind !== 'interval' || event.allDay || event.end === null) return null;
  const from = splitLocal(event.start);
  const to = splitLocal(event.end);
  const start = from.date === day ? from.time : '00:00';
  const end = to.date === day ? to.time : '24:00';
  return `${start}–${end}`;
}

function utcMs(date: string): number {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
}

/** What a day holds: events touching it, and the minutes actually occupied by timed ones. */
export interface DayLoad {
  /** Events (timed and all-day) that touch the day; deadline-only tasks are not counted. */
  count: number;
  /** The UNION of the timed intervals on that day (overlaps counted once), in minutes. */
  minutes: number;
}

const DAY_MINUTES = 24 * 60;

function minuteOfDay(time: string): number {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Daily workload of the given days from calendar events. Only events (intervals) count: a
 * deadline-only task occupies no time, and completion is not workload. Occupied time is the
 * union of the timed intervals clipped to the day, so two overlapping meetings are not added up.
 */
export function workloadByDay(
  events: readonly CalendarEvent[],
  days: readonly string[],
): Map<string, DayLoad> {
  const result = new Map<string, DayLoad>();
  const intervals = events.filter((event) => event.kind === 'interval' && event.end !== null);
  for (const day of days) {
    const segments: [number, number][] = [];
    let count = 0;
    for (const event of intervals) {
      if (!(event.start.slice(0, 10) <= day && day <= lastDayOf(event))) continue;
      count += 1;
      if (event.allDay || event.end === null) continue;
      const from = splitLocal(event.start);
      const to = splitLocal(event.end);
      const start = from.date === day ? minuteOfDay(from.time) : 0;
      const end = to.date === day ? minuteOfDay(to.time) : DAY_MINUTES;
      if (end > start) segments.push([start, end]);
    }
    segments.sort((a, b) => a[0] - b[0]);
    let minutes = 0;
    let reach = -1;
    let begin = 0;
    for (const [start, end] of segments) {
      if (start > reach) {
        if (reach >= 0) minutes += reach - begin;
        begin = start;
        reach = end;
      } else if (end > reach) {
        reach = end;
      }
    }
    if (reach >= 0) minutes += reach - begin;
    result.set(day, { count, minutes });
  }
  return result;
}
